/**
 * @file Home.jsx
 * @description "Bookend" hero composition for AI Expo Jordan 2026.
 *
 *   Concept:
 *   "AI EXPO" stretches as a single edge-to-edge row of massive type.
 *   Two character figures sit at the far left and far right edges,
 *   overlapping the wordmark in front (left character covers the start
 *   of "AI", right character covers the end of "EXPO"). The eye reads
 *   the type through the figures — a trick borrowed from editorial
 *   magazine covers and concert posters.
 *
 *   A small, refined countdown sits beneath the wordmark, centered.
 *
 *   Structure:
 *   - Top status bar
 *   - Stage:
 *       · LEFT  character (anchored to left edge, in front)
 *       · RIGHT character (anchored to right edge, in front)
 *       · "AI EXPO" massive single-row wordmark, behind the characters
 *       · Small timer pill below the wordmark
 *       · Edition stamp top-center, tagline below the timer
 *   - Bottom credit bar
 *
 *   Character images:
 *     Drop two transparent-background PNGs in your assets folder, then
 *     point CHAR_LEFT and CHAR_RIGHT at them. The component falls back
 *     to abstract silhouettes if either is null, so the layout works
 *     before the assets are ready.
 *
 * @version 7.0.0
 * @author AI Expo Jordan 2026 Development Team
 */

import React, { useEffect, useRef, useState } from 'react';
import '../ComponentsStyling/Home.css';

/* ------------------------------------------------------------------ */
/* Configuration — drop your character art here                        */
/* ------------------------------------------------------------------ */
/* Use transparent-background PNG/WebP images for both characters.
 * Recommended: characters facing inward (toward the center).
 * If null, an abstract neon silhouette fallback renders instead.
 */
const CHAR_LEFT = '/images/home/white.webp';
const CHAR_RIGHT =  '/images/home/blue.webp';

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useSpotlight = (ref) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;

        let raf = 0;
        let pending = null;

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            pending = {
                x: ((e.clientX - rect.left) / rect.width) * 100,
                y: ((e.clientY - rect.top) / rect.height) * 100,
            };
            if (!raf) {
                raf = requestAnimationFrame(() => {
                    if (pending) {
                        el.style.setProperty('--mx', pending.x + '%');
                        el.style.setProperty('--my', pending.y + '%');
                    }
                    raf = 0;
                });
            }
        };

        el.addEventListener('mousemove', onMove);
        return () => {
            el.removeEventListener('mousemove', onMove);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [ref]);
};

/* ------------------------------------------------------------------ */
/* Character figure — image when provided, abstract silhouette otherwise */
/* ------------------------------------------------------------------ */

const CharacterFallback = ({ side }) => (
    /* Stylised neon silhouette — vertical light beams in the brand colors.
       Acts as a placeholder until real character art is dropped in. */
    <svg
        className="hm__char-fallback"
        viewBox="0 0 200 500"
        preserveAspectRatio={side === 'left' ? 'xMinYMax meet' : 'xMaxYMax meet'}
        aria-hidden
    >
        <defs>
            <linearGradient
                id={`hm-char-${side}`}
                x1="0%" y1="0%" x2="0%" y2="100%"
            >
                <stop offset="0%"
                    stopColor={side === 'left' ? '#2cabe2' : '#7a3cff'}
                    stopOpacity="0" />
                <stop offset="40%"
                    stopColor={side === 'left' ? '#2cabe2' : '#7a3cff'}
                    stopOpacity="0.85" />
                <stop offset="100%"
                    stopColor={side === 'left' ? '#7a3cff' : '#2cabe2'}
                    stopOpacity="1" />
            </linearGradient>
            <filter id={`hm-char-glow-${side}`}>
                <feGaussianBlur stdDeviation="6" result="b" />
                <feMerge>
                    <feMergeNode in="b" />
                    <feMergeNode in="SourceGraphic" />
                </feMerge>
            </filter>
        </defs>

        {/* Vertical beam stack */}
        <g filter={`url(#hm-char-glow-${side})`}>
            <rect x="80" y="40" width="6" height="460"
                fill={`url(#hm-char-${side})`} opacity="0.7" />
            <rect x="100" y="80" width="3" height="420"
                fill={`url(#hm-char-${side})`} opacity="0.5" />
            <rect x="115" y="20" width="8" height="480"
                fill={`url(#hm-char-${side})`} opacity="0.9" />
            <rect x="135" y="60" width="4" height="440"
                fill={`url(#hm-char-${side})`} opacity="0.4" />
        </g>

        {/* Floor highlight */}
        <ellipse cx="110" cy="495" rx="100" ry="6"
            fill={side === 'left' ? '#2cabe2' : '#7a3cff'}
            opacity="0.4"
            filter={`url(#hm-char-glow-${side})`} />
    </svg>
);

const Character = ({ side, src }) => (
    <div className={`hm__char hm__char--${side}`}>
        {/* Soft underglow disc behind the character */}
        <span className={`hm__char-glow hm__char-glow--${side}`} aria-hidden />

        {/* Character image (or fallback) */}
        {src ? (
            <img
                src={src}
                alt=""
                className="hm__char-img"
                draggable={false}
                decoding="async"
                loading="eager"
                fetchpriority={side === 'left' ? 'high' : 'low'}
            />
        ) : (
            <CharacterFallback side={side} />
        )}

    </div>
);

/* ------------------------------------------------------------------ */
/* Small timer pill                                                    */
/* ------------------------------------------------------------------ */

const TimerPill = ({ days, hours, mins, secs }) => (
    <div className="hm__timer" role="timer" aria-label="Countdown to day one of AI Expo Jordan 2026">
        <span className="hm__timer-corner hm__timer-corner--tl" />
        <span className="hm__timer-corner hm__timer-corner--tr" />
        <span className="hm__timer-corner hm__timer-corner--bl" />
        <span className="hm__timer-corner hm__timer-corner--br" />

        <div className="hm__timer-head">
            <span className="hm__timer-head-pulse" />
            <span className="hm__timer-head-text">DAY ONE BEGINS</span>
            <span className="hm__timer-head-line" />
            <span className="hm__timer-head-target">3 OCTOBER 2026</span>
        </div>

        <div className="hm__timer-body">
            <div className="hm__timer-cell">
                <span className="hm__timer-val">{days}</span>
                <span className="hm__timer-lbl">DAYS</span>
            </div>
            <span className="hm__timer-colon">:</span>
            <div className="hm__timer-cell">
                <span className="hm__timer-val">{hours}</span>
                <span className="hm__timer-lbl">HRS</span>
            </div>
            <span className="hm__timer-colon">:</span>
            <div className="hm__timer-cell">
                <span className="hm__timer-val">{mins}</span>
                <span className="hm__timer-lbl">MIN</span>
            </div>
            <span className="hm__timer-colon">:</span>
            <div className="hm__timer-cell hm__timer-cell--hl">
                <span className="hm__timer-val">{secs}</span>
                <span className="hm__timer-lbl">SEC</span>
            </div>
        </div>

        <span className="hm__timer-scan" aria-hidden />
    </div>
);

/* ------------------------------------------------------------------ */
/* Home                                                                */
/* ------------------------------------------------------------------ */

const Home = () => {
    const ref = useRef(null);
    useSpotlight(ref);

    /* Counts down to the opening of Day 1 — 1 October 2026, Amman time. */
    const target = new Date('2026-10-03T09:00:00+03:00').getTime();
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const diff = Math.max(0, target - now);
    const days = Math.floor(diff / 86_400_000);
    const hours = Math.floor((diff / 3_600_000) % 24);
    const mins = Math.floor((diff / 60_000) % 60);
    const secs = Math.floor((diff / 1000) % 60);
    const pad = (n, w = 2) => String(n).padStart(w, '0');

    const cdProps = {
        days: pad(days, 3),
        hours: pad(hours),
        mins: pad(mins),
        secs: pad(secs),
    };

    return (
        <section className="hm" id="home" ref={ref}>
            {/* Atmospheric background layers */}
            <div className="hm__bg-grid" aria-hidden />
            <div className="hm__bg-spot" aria-hidden />
            <div className="hm__bg-noise" aria-hidden />
            <div className="hm__bg-glow hm__bg-glow--azure" aria-hidden />
            <div className="hm__bg-glow hm__bg-glow--purple" aria-hidden />

            {/* TOP STATUS BAR */}
            <header className="hm__statusbar">
                <div className="hm__statusbar-l">
                    <span className="hm__statusbar-id">SYS&nbsp;//&nbsp;AIEXPO·26</span>
                    <span className="hm__statusbar-divider" />
                    <span className="hm__statusbar-pulse" />
                    <span className="hm__statusbar-live">LIVE</span>
                </div>

                <div className="hm__statusbar-c">
                    <span className="hm__statusbar-text">
                        IEEE&nbsp;CIS&nbsp;UJ&nbsp;·&nbsp;UNIVERSITY&nbsp;OF&nbsp;JORDAN
                    </span>
                </div>

                <div className="hm__statusbar-r">
                    <span className="hm__statusbar-text hm__statusbar-text--mute">EDITION&nbsp;01</span>
                    <span className="hm__statusbar-frame">JORDAN</span>
                </div>
            </header>

            {/* MAIN STAGE */}
            <div className="hm__stage">

                {/* Corner ornaments — frame the canvas without boxing it */}
                <span className="hm__corner hm__corner--tl" aria-hidden />
                <span className="hm__corner hm__corner--tr" aria-hidden />
                <span className="hm__corner hm__corner--bl" aria-hidden />
                <span className="hm__corner hm__corner--br" aria-hidden />

                {/* Edition stamp — top, centered */}
                <div className="hm__stamp">
                    <span className="hm__stamp-bracket">[</span>
                    <span className="hm__stamp-text">
                        AI&nbsp;EXPO&nbsp;·&nbsp;JORDAN&nbsp;·&nbsp;2026
                    </span>
                    <span className="hm__stamp-bracket">]</span>
                </div>

                {/* CENTER STACK — wordmark + timer + tagline */}
                <div className="hm__center">

                    {/* THE WORDMARK — single edge-to-edge row */}
                    <h1 className="hm__wordmark" aria-label="AI Expo Jordan 2026">
                        <span className="hm__wordmark-word hm__wordmark-word--outline-white">AI</span>
                        <span className="hm__wordmark-space" aria-hidden>
                            <span className="hm__wordmark-space-tick" />
                            <span className="hm__wordmark-space-line" />
                            <span className="hm__wordmark-space-tick" />
                        </span>
                        {/* The X is kerned on its own — see Home.css. Oswald Bold
                            draws it with almost no left side bearing, so without
                            this it jams against the E. */}
                        <span className="hm__wordmark-word hm__wordmark-word--outline">
                            E<span className="hm__wordmark-kern-x">X</span>PO
                        </span>
                    </h1>

                    {/* SMALL TIMER below the wordmark */}
                    <TimerPill {...cdProps} />

                    {/* Tagline below the timer */}
                    <div className="hm__tagline">
                        <span className="hm__tagline-bar" />
                        <span className="hm__tagline-text">
                            JORDAN&apos;S&nbsp;AI&nbsp;EXHIBITION&nbsp;&amp;&nbsp;COMPETITION
                        </span>
                        <span className="hm__tagline-bar" />
                    </div>

                </div>

                {/* CHARACTERS — bookends, in front of the wordmark */}
                <Character side="left" src={CHAR_LEFT} />
                <Character side="right" src={CHAR_RIGHT} />

            </div>

            {/* BOTTOM CREDIT BAR */}
            <footer className="hm__creditbar">
                <span className="hm__creditbar-text">
                    AI&nbsp;EXPO&nbsp;JORDAN&nbsp;·&nbsp;EDITION&nbsp;01&nbsp;·&nbsp;© 2026
                </span>
                <span className="hm__creditbar-line" />
                <span className="hm__creditbar-scroll">
                    SCROLL&nbsp;TO&nbsp;EXPLORE
                    <span className="hm__creditbar-arrow" aria-hidden>
                        <svg viewBox="0 0 16 16" fill="none">
                            <path d="M8 2v12m0 0L3 9m5 5l5-5"
                                stroke="currentColor" strokeWidth="1.4"
                                strokeLinecap="square" />
                        </svg>
                    </span>
                </span>
            </footer>
        </section>
    );
};

export default Home;